import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useTenant } from "./useTenant";
import { playNotificationChime } from "@/lib/notification-sound";

export interface NotificationRow {
  id: string;
  tenant_id: string;
  recipient_user_id: string;
  type: string;
  title: string;
  body: string | null;
  actor_email: string | null;
  actor_user_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  entity_unique_id: string | null;
  read_at: string | null;
  created_at: string;
}

const LIST_LIMIT = 50;

export function useNotifications() {
  const { user } = useAuth();
  const { tenantId, tenantReady } = useTenant();

  return useQuery({
    queryKey: ["notifications", "list", user?.id, tenantId],
    enabled: !!user?.id && tenantReady && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("recipient_user_id", user!.id)
        .eq("tenant_id", tenantId!)
        .order("created_at", { ascending: false })
        .limit(LIST_LIMIT);
      if (error) throw error;
      return (data ?? []) as NotificationRow[];
    },
  });
}

export function useUnreadNotificationCount() {
  const { user } = useAuth();
  const { tenantId, tenantReady } = useTenant();

  return useQuery({
    queryKey: ["notifications", "unread-count", user?.id, tenantId],
    enabled: !!user?.id && tenantReady && !!tenantId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("recipient_user_id", user!.id)
        .eq("tenant_id", tenantId!)
        .is("read_at", null);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", id)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { tenantId } = useTenant();
  return useMutation({
    mutationFn: async () => {
      if (!user?.id || !tenantId) return;
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("recipient_user_id", user.id)
        .eq("tenant_id", tenantId)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

/**
 * Subscribe to realtime changes on the notifications table for the current
 * user. Invalidates the list and unread-count queries on any insert/update.
 * Mount this once at app/layout level.
 */
export function useNotificationRealtime() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `recipient_user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            playNotificationChime();
          }
          queryClient.invalidateQueries({ queryKey: ["notifications"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);
}